import type { FC } from "react"
import React from "react"
import type { ButtonProps } from "@chakra-ui/react"
import { Button, useDisclosure } from "@chakra-ui/react"

import { useAddress } from "web3/hooks/useAddress"
import ErrorBoundary from "common/components/ErrorBoundary"
import PreCheckModal from "web3/components/PreCheckModal"
import type { ChainId } from "query/graphql"
import { getChainIdParams } from "web3/helpers/chainId"
import { useChainReference } from "web3/hooks/useChainReference"
import type { Chain } from "web3/hooks/useMultichainAccount"

type Props = {
  onValid: () => void
  requiredChainId: ChainId
  isDisabled?: boolean
  isMultiChain?: boolean
  walletType?: Chain
}

const Web3Button: FC<Props & ButtonProps> = ({
  onValid,
  requiredChainId,
  children,
  isDisabled = false,
  isMultiChain = false,
  onClick,
  walletType,
  ...buttonProps
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const address = useAddress()
  const walletChainReference = useChainReference()

  const { reference: requiredChainReference } =
    getChainIdParams(requiredChainId)
  const isCorrectNetwork = walletChainReference === requiredChainReference

  const handleButtonClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (onClick) {
      onClick(event)

      return
    }

    if (!address) {
      onOpen()

      return
    }

    if (!isCorrectNetwork) {
      onOpen()

      return
    }

    if (!isMultiChain) {
      onClose()
    }

    onValid()
  }

  if (!children) return null

  return (
    <ErrorBoundary section="ProposalVoteButton">
      <PreCheckModal
        chainId={requiredChainId}
        isCorrectNetwork={isCorrectNetwork}
        isOpen={isOpen}
        walletType={walletType}
        onClose={onClose}
        onValid={onValid}
      />
      <Button
        isDisabled={isDisabled}
        pos="relative"
        variant="primary"
        onClick={handleButtonClick}
        {...buttonProps}
      >
        {children}
      </Button>
    </ErrorBoundary>
  )
}

export default Web3Button
